import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button, Box } from "@mui/material";
import { displayInfoMessage, displayErrorMessage } from "@agricircle/shared/redux";
import { useModal } from "@agricircle/shared/hooks";
import { selectFarm } from "@agricircle/shared/farms/redux";
import { FieldsSelector } from "@agricircle/shared/farms/components";
import { Subheading } from "@agricircle/shared/widgets";
import { KitStatus } from "../kit_status";
import { SoilStatus } from "./soil_status";
import { RequestSoilSamplingModal, ImportMeasurementsModal } from "./sampling";
import { useSamplingKitApi } from "../../hooks/sampling_kit";


export default () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { openModal } = useModal();
    const farm = useSelector(selectFarm);
    const samplingKitApi = useSamplingKitApi();
    const [kitParameters, setKitParameters] = useState({ area: 0, fieldIds: [] });
    const [kitStatus, setKitStatus] = useState();

    useEffect(() => {
        if (!farm?.id) return;
        samplingKitApi.farmKitStatus(farm.id, setKitStatus);
    }, [farm?.id]);

    if (farm?.fields == null) return null;

    function handleLabResultsImport() {
        openModal(
            null,
            <ImportMeasurementsModal />,
            handleMeasurements
        );
    }

    function handleMeasurements(formData) {
        samplingKitApi.parseXlsxMeasurementsFile(
            formData,
            measurements => samplingKitApi.uploadMeasurements(measurements, () => dispatch(displayInfoMessage(t("measurements-imported"))))
        );
    }

    function onChangeFieldSelection(fieldIds) {
        const newParameters = { area: 0, fieldIds: [] };
        if (fieldIds.length > 0) {
            let unSelectable = false;
            farm.fields.forEach(f => {
                if (fieldIds.includes(f.id)) {
                    newParameters.fieldIds.push(f.id);
                    newParameters.area += f.area;
                }
            });
            if (unSelectable && fieldIds.length == 1)
                dispatch(displayErrorMessage(t("samplingKit.errors.no-info")));
        }
        setKitParameters(newParameters);
        return newParameters.fieldIds;
    }

    function handlePrepareKit(e) {
        if (e.shiftKey) {
            confirm(
                t("samplingKit.measurements-regenerate"),
                () => {
                    samplingKitApi.regenerateSamplingKit(farm.id, kitParameters.fieldIds, onChange);
                }
            );
        } else {
            openModal(
                null,
                <RequestSoilSamplingModal />,
                (measurements, depths) => {
                    samplingKitApi.prepareKit(farm.id,
                        kitParameters.fieldIds,
                        measurements,
                        depths,
                        setKitStatus
                    );
                },
                { farmId: farm.id, kitParameters }
            );
        }
    }

    const headers = [t("shared:farms.field-name-label"), kitParameters?.area ? t("kit.selected-area", { area: kitParameters.area.toFixed(1) }) : t("shared:farms.area-label")];
    const actions = (f) => (<SoilStatus soil={f.soil} icon="fa-route" property="sampling_path" />);

    return (<Box sx={{ padding: 1 }}>
        <Subheading title={t("samplingKit.title")}>
            {kitParameters?.area
                ? <Button
                    variant="contained"
                    data-cy="button-prepare-sampling-kit"
                    onClick={handlePrepareKit}
                    disabled={kitParameters.area === 0 || Boolean(kitStatus)}
                >
                    {t("kit.prepare-btn")}
                </Button>
                : <Button
                    variant="contained"
                    data-cy="button-upload-lab-results"
                    onClick={handleLabResultsImport}
                    color="primary"
                    size="small"
                >
                    {t("samplingKit.measurements-upload-btn")}
                </Button>
            }
        </Subheading>

        <KitStatus
            status={kitStatus}
            farmId={farm.id}
            kitStatusRequest={samplingKitApi.kitStatus}
            kitCancelRequest={samplingKitApi.cancelKit}
            onChange={setKitStatus}
        />
        <FieldsSelector
            fields={farm.fields}
            headers={headers}
            actions={actions}
            selected={kitParameters?.fieldIds || []}
            onChange={onChangeFieldSelection}
        />
    </Box>);
};
